import { useState } from 'react';
import { CheckCircle2, Clock, Send, ThumbsDown, ThumbsUp, XCircle } from 'lucide-react';
import { Button } from '@/components/Button';
import { Field, fieldInputClass } from '@/components/Field';
import { EmptyState } from '@/components/States';
import { useApprovals, useApprove, useReject, useRequestApproval } from '@/hooks/use-campaign-flow';
import { useApprovers } from '@/hooks/use-users';
import { describeError } from '@/lib/describe-error';
import { formatDateTime } from '@/lib/utils';
import { Roles, type Session } from '@/auth/keycloak';
import type { CampaignDetail } from '@/lib/schemas';

/**
 * The approval gate (spec § 4.1, § 8).
 *
 * ── SEPARATION OF DUTIES IS THE SERVER'S JOB, NOT THIS COMPONENT'S ────────────────
 *
 * The service enforces all of it: you cannot assign an approval to yourself, you cannot decide one
 * assigned to somebody else, and you cannot approve your own most recent edit. Admin is deliberately
 * not an exception, because an Admin holds every role and a role check would let them wave their own
 * campaign through.
 *
 * So this component hides the decide buttons from people who are not the assigned approver purely as
 * an affordance — showing a button that always 403s is worse than not showing it. It is not a control,
 * and nothing here should be read as one.
 *
 * ── AN INVALIDATED APPROVAL IS SHOWN, NOT HIDDEN ──────────────────────────────────
 *
 * The API keeps invalidated rows rather than deleting them, because "this was approved and then
 * edited" is precisely what somebody auditing a send needs to see. A UI that dropped them would make
 * the API's care pointless — so they are rendered, struck through, with the reason.
 *
 * ── REQUESTING WAS THE MISSING HALF ───────────────────────────────────────────────
 *
 * This panel could decide an approval and not ask for one: `useRequestApproval` existed with no caller,
 * because requesting means naming a person and a token carries only its own subject. So a brand with
 * `requiresApproval` set could never send anything — the checklist blocked on an approval nobody could
 * request. The approver picker is fed by the directory, which is built from sign-ins (see DirectoryUser),
 * and the empty case says so rather than showing an empty select.
 */
export function ApprovalPanel({
  campaign,
  session,
}: {
  campaign: CampaignDetail;
  session: Session;
}) {
  const approvals = useApprovals(campaign.id);
  const approve = useApprove(campaign.id);
  const reject = useReject(campaign.id);

  const [comment, setComment] = useState('');

  // Newest first, and the one that matters is the open request: no decision, not invalidated.
  const open = approvals.data?.find(
    (approval) => approval.isApproved === null && approval.invalidatedAt === null,
  );

  const decided = (approvals.data ?? []).filter(
    (approval) => approval.isApproved !== null || approval.invalidatedAt !== null,
  );

  const canRequest =
    (session.roles.includes(Roles.Author) || session.roles.includes(Roles.Admin)) &&
    (campaign.status === 'Draft' || campaign.status === 'InReview');

  // An approvals request that FAILED must not read as "no approval needed". This panel is the only
  // place the send screen says a campaign is waiting on somebody, so rendering nothing on error would
  // make a blocked campaign look like an unblocked one — and the send button next to it would then be
  // the first thing to mention it (ui-ux.md § 5).
  if (approvals.isError) {
    return (
      <section className="mt-5 rounded-lg border border-border bg-surface p-5">
        <h2 className="text-sm font-semibold text-heading">Approval</h2>

        <p role="alert" className="mt-3 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          {describeError(approvals.error).message} Until this loads, treat the approval state as unknown
          rather than as settled.
        </p>

        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={() => void approvals.refetch()}>
            Try again
          </Button>
        </div>
      </section>
    );
  }

  if (approvals.data === undefined) return null;

  // Nothing outstanding, no history, and nothing this person could do about it.
  if (approvals.data.length === 0 && !canRequest) return null;

  const isAssignedToMe = open !== undefined && open.approverSubject === session.subject;
  const isPending = approve.isPending || reject.isPending;
  const error = approve.error ?? reject.error;

  return (
    <section className="mt-5 rounded-lg border border-border bg-surface p-5">
      <h2 className="text-sm font-semibold text-heading">Approval</h2>

      {open !== undefined && (
        <div className="mt-3">
          <p className="flex flex-wrap items-center gap-2 text-sm text-foreground">
            <Clock className="size-4 shrink-0 text-warning-ink" aria-hidden="true" />
            Waiting on <strong className="font-semibold">{open.approverEmail}</strong>
            <span className="text-muted">since {formatDateTime(open.requestedAt)}</span>
          </p>

          {open.comment !== null && (
            <p className="mt-1 pl-6 text-sm text-muted">“{open.comment}”</p>
          )}

          {isAssignedToMe ? (
            <div className="mt-4 flex flex-col gap-3">
              {error !== null && (
                <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink" role="alert">
                  {describeError(error).message}
                </p>
              )}

              <Field
                id="approval-comment"
                label="Comment"
                hint="Optional on approval, and the useful part of a rejection."
              >
                <input
                  id="approval-comment"
                  type="text"
                  value={comment}
                  maxLength={2000}
                  onChange={(event) => setComment(event.target.value)}
                  aria-describedby="approval-comment-hint"
                  placeholder="Looks good"
                  className={fieldInputClass}
                />
              </Field>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  disabled={isPending}
                  onClick={() => approve.mutate(comment.trim() === '' ? null : comment.trim())}
                >
                  <ThumbsUp className="size-4" aria-hidden="true" />
                  {approve.isPending ? 'Approving…' : 'Approve'}
                </Button>

                <Button
                  variant="secondary"
                  disabled={isPending}
                  onClick={() => reject.mutate(comment.trim() === '' ? null : comment.trim())}
                >
                  <ThumbsDown className="size-4" aria-hidden="true" />
                  {reject.isPending ? 'Rejecting…' : 'Reject'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-2 pl-6 text-xs text-muted">
              Only the assigned approver can decide this. Reassign it if they are unavailable, rather
              than deciding on their behalf.
            </p>
          )}
        </div>
      )}

      {/* The ask. Offered when nothing is outstanding — a second request supersedes the first
          server-side, but presenting that as the primary action would invite reassigning by accident. */}
      {open === undefined && canRequest && <RequestApproval campaign={campaign} />}

      {decided.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
          {decided.map((approval) => (
            <li key={approval.id} className="flex items-start gap-2 text-sm">
              {approval.invalidatedAt !== null ? (
                <XCircle className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden="true" />
              ) : approval.isApproved === true ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success-ink" aria-hidden="true" />
              ) : (
                <XCircle className="mt-0.5 size-4 shrink-0 text-danger-ink" aria-hidden="true" />
              )}

              <span className="min-w-0">
                <span className={approval.invalidatedAt === null ? '' : 'line-through text-muted'}>
                  {approval.isApproved === true ? 'Approved' : 'Rejected'} by {approval.approverEmail}
                  {approval.decidedAt !== null && ` on ${formatDateTime(approval.decidedAt)}`}
                </span>

                {approval.comment !== null && (
                  <span className="block text-muted">“{approval.comment}”</span>
                )}

                {/* The reason it stopped counting. Without this, an approval that silently vanished
                    from effect looks like a bug rather than the rule working. */}
                {approval.invalidationReason !== null && (
                  <span className="block text-xs text-warning-ink">
                    No longer valid: {approval.invalidationReason}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Asking a named colleague to approve.
 *
 * ── WHY A PICKER AND NOT A FREE-TEXT ADDRESS ──────────────────────────────────────
 *
 * The endpoint takes a subject AND an email, and the subject is what the decision is checked against.
 * A typed email could not produce a subject, so a free-text field would either need a lookup — the same
 * directory by another name — or would let somebody assign an approval to a person who cannot sign in
 * to act on it. The picker guarantees both halves come from the same record.
 *
 * ── THE EMPTY CASE IS EXPLAINED, NOT HIDDEN ───────────────────────────────────────
 *
 * The directory is built from sign-ins, so it is legitimately empty on a fresh install and thin early
 * on. An empty select would read as a broken screen; saying that approvers appear once they have signed
 * in turns it into an instruction.
 */
function RequestApproval({ campaign }: { campaign: CampaignDetail }) {
  const approvers = useApprovers(true);
  const submit = useRequestApproval(campaign.id);

  const [subject, setSubject] = useState('');
  const [note, setNote] = useState('');

  const chosen = (approvers.data ?? []).find((candidate) => candidate.subject === subject);

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (chosen === undefined) return;

    submit.mutate(
      {
        approverSubject: chosen.subject,
        approverEmail: chosen.email,
        comment: note.trim() === '' ? null : note.trim(),
      },
      {
        onSuccess: () => {
          setSubject('');
          setNote('');
        },
      },
    );
  }

  if (approvers.isPending) return null;

  if (approvers.data !== undefined && approvers.data.length === 0) {
    return (
      <div className="mt-3 border-t border-border pt-3">
        <EmptyState
          icon={Send}
          title="Nobody to ask yet"
          description="Approvers appear here once they have signed in to Campaigns at least once — that is how the app learns who they are. Ask a colleague with the Approver role to sign in, then come back."
        />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-3 flex flex-col gap-3 border-t border-border pt-3" noValidate>
      {submit.isError && (
        <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          {describeError(submit.error).message}
        </p>
      )}

      <Field
        id="approver"
        label="Ask someone to approve"
        hint="They get an email. You cannot pick yourself — a second pair of eyes is the only thing approval provides."
      >
        <select
          id="approver"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          aria-describedby="approver-hint"
          className={fieldInputClass}
        >
          <option value="">Choose a colleague…</option>
          {(approvers.data ?? []).map((candidate) => (
            <option key={candidate.subject} value={candidate.subject}>
              {candidate.displayName} — last here {formatDateTime(candidate.lastSeenAt)}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id="approval-note"
        label="Note"
        hint="Optional. What you want them to look at."
      >
        <input
          id="approval-note"
          type="text"
          value={note}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          aria-describedby="approval-note-hint"
          placeholder="Ready for the March send — check the offer wording"
          className={fieldInputClass}
        />
      </Field>

      <div>
        <Button type="submit" variant="primary" disabled={submit.isPending || chosen === undefined}>
          <Send className="size-4" aria-hidden="true" />
          {submit.isPending ? 'Requesting…' : 'Request approval'}
        </Button>
      </div>
    </form>
  );
}
